import React, { useState, useEffect } from 'react';
import { Heart, TrendingUp, Dumbbell, Briefcase, User, DollarSign, Users, Flame, Target, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SelfImprovement = () => {
  const [selfImprovement, setSelfImprovement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSelfImprovement();
  }, []);

  const fetchSelfImprovement = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/self-improvement`);
      setSelfImprovement(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching self-improvement topics:', err);
      setError('Failed to load content. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const categoryIcons = {
    'Personal Growth': TrendingUp,
    'Marriage': Heart,
    'Physical Health': Dumbbell,
    'Work-Life Balance': Briefcase,
    'Single Dad Self-Care': User,
    'Financial Wellness': DollarSign,
    'Male Friendship': Users,
    'Anger Management': Flame,
    'Purpose and Legacy': Target
  };

  const categoryColors = {
    'Personal Growth': 'from-purple-400 to-pink-500',
    'Marriage': 'from-red-400 to-pink-500',
    'Physical Health': 'from-green-400 to-teal-500',
    'Work-Life Balance': 'from-blue-400 to-indigo-500',
    'Single Dad Self-Care': 'from-cyan-400 to-blue-500',
    'Financial Wellness': 'from-emerald-400 to-green-500',
    'Male Friendship': 'from-amber-400 to-orange-500',
    'Anger Management': 'from-red-500 to-orange-600',
    'Purpose and Legacy': 'from-violet-400 to-purple-500'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-xl text-gray-600">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchSelfImprovement}
            className="px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent">
              Dad Self-Improvement
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            You can't pour from an empty cup. Invest in yourself to be the best father, partner, and man you can be.
          </p>
        </div>

        {/* Quote Section */}
        <Card className="mb-12 bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
          <CardContent className="p-8 text-center">
            <blockquote className="text-2xl font-bold italic mb-4">
              "Your children are watching how you treat yourself. Show them what self-respect and growth look like."
            </blockquote>
            <p className="text-white/90">— Andrew Garcia</p>
          </CardContent>
        </Card>

        {/* Self-Improvement Topics */}
        <div className="space-y-8">
          {selfImprovement.map((topic, index) => {
            const IconComponent = categoryIcons[topic.category];
            const colorGradient = categoryColors[topic.category];

            return (
              <Card 
                key={topic.id} 
                className="border-2 hover:border-amber-300 transition-all duration-300 hover:shadow-xl overflow-hidden"
              >
                <div className={`h-2 bg-gradient-to-r ${colorGradient}`} />
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colorGradient} flex items-center justify-center flex-shrink-0`}>
                      {IconComponent && <IconComponent className="h-7 w-7 text-white" />}
                    </div>
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-amber-600 uppercase tracking-wide">
                        {topic.category}
                      </span>
                      <h2 className="text-2xl font-bold text-gray-900 mt-1 mb-2">{topic.title}</h2>
                      <p className="text-gray-600 font-medium">{topic.description}</p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 leading-relaxed">{topic.content}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Marriage Section - Enhanced */}
        <Card className="mt-12 border-2 border-red-200 bg-gradient-to-br from-red-50 to-pink-50">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-400 to-pink-500 flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-white" fill="currentColor" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Strengthen Your Marriage</h3>
              <p className="text-gray-600 max-w-2xl mx-auto">
                A strong marriage is the foundation of a happy family. When kids see their parents in a loving, respectful relationship, it shapes their understanding of healthy relationships for life.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {[
                {
                  title: 'Weekly Date Nights',
                  description: 'Protect this time like a business meeting. Your relationship needs nurturing.'
                },
                {
                  title: 'Communicate Openly',
                  description: 'Share your feelings, fears, and dreams. Vulnerability builds intimacy.'
                },
                {
                  title: 'Fight Fair',
                  description: 'Disagree respectfully. Never attack character, focus on the issue.'
                },
                {
                  title: 'Show Appreciation',
                  description: 'Thank your partner daily. Small gestures of gratitude go a long way.'
                }
              ].map((tip, index) => (
                <div key={index} className="bg-white p-6 rounded-lg border-2 border-red-200 hover:border-red-300 transition-colors">
                  <h4 className="font-bold text-lg text-gray-900 mb-2">{tip.title}</h4>
                  <p className="text-gray-600">{tip.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Action CTA */}
        <Card className="mt-12 bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Level Up?</h3>
            <p className="mb-6 text-white/90 max-w-2xl mx-auto">
              Get personalized coaching and accountability through our mentorship program. Andrew Garcia will help you become the man, father, and partner you aspire to be.
            </p>
            <a href="/mentorship">
              <button className="px-8 py-3 bg-white text-orange-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg">
                Explore Mentorship Program
              </button>
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SelfImprovement;